import { Link } from 'react-router'
import { Icon } from '../components/Icon'
import { useShare } from '../lib/useShare'

/**
 * The origin story. Static copy — there is no CMS behind this page on purpose,
 * because it changes about once a year and putting it in the database would mean
 * an admin screen nobody would ever open.
 */
const CHAPTERS = [
  {
    n: '01',
    title: 'Max started saying things',
    body: [
      `Max is a great guy. He is also wired to an antenna the rest of us cannot pick
       up, and the broadcasts do not stop. Dolphins quietly running the planet while
       we act as their cameras. Cheese being poisonous on a technicality nobody wants
       to litigate. Fans breaking because primordial beings mistake the spinning for
       a teleporter and shut it down.`,
      `The supply was never the problem. The problem was that every one of these
       arrived at one in the morning in somebody's kitchen, got laughed at, and then
       evaporated. We would try to retell one a week later and get maybe forty percent
       of it. That is a real loss. You cannot paraphrase a man who says "you never see
       a cold bird" and expect it to survive the trip.`,
    ],
  },
  {
    n: '02',
    title: 'So we started recording',
    body: [
      `This site is the record. Every theory gets a description, a proper write-up of
       what was actually claimed, and — where we have it — the audio, so you can hear
       him arrive at it in real time. That last part matters more than we expected.
       Reading a theory is funny. Hearing the exact moment the logic goes over the
       cliff is funnier.`,
      `Everything gets a credibility rating out of 100. The rating is made up. We take
       assigning it very seriously.`,
    ],
  },
  {
    n: '03',
    title: 'What counts as a theory',
    body: [
      `Not everything Max says gets in. A theory has to explain something. "Cheese is
       disgusting" is an opinion and goes nowhere. "Cheese is poisonous because it is
       rotten milk, which is also why you must never keep it in a house with Febreze"
       is a theory, because there is a mechanism, the mechanism is doing real work,
       and it is wrong in a way you can follow all the way to the end.`,
      `The second requirement is that he means it. Anybody can be deliberately absurd
       for a laugh and it is never as good. "Dinosaurs never used spoons" is in the
       archive because it was offered as evidence, in support of a larger point, to a
       room that had not asked. The bar is not how strange it sounds. The bar is
       whether he would defend it.`,
    ],
  },
  {
    n: '04',
    title: 'Episodes only come out here',
    body: [
      `No Spotify. No Apple Podcasts. No YouTube. Every episode is published on this
       website and nowhere else, and that is a deliberate choice rather than a
       to-do item we have not got to yet.`,
      `We own this one. Nothing here gets buried by a recommendation algorithm,
       demonetised for tone, taken down by a system with no appeal process, or wrapped
       in three ads for something neither of us has heard of. It costs us a bit every
       month to keep the lights on and it is worth it. If you want the episodes, this
       is the address. That is the whole distribution strategy.`,
      `There is an RSS feed, which is the one exception and not really an exception at
       all — it is our feed, on our domain, and it works in whatever app you already
       use. Nobody sits in the middle of it deciding what you get to see.`,
    ],
  },
] as const

export function Story() {
  const { share, copied } = useShare()

  return (
    <article className="story">
      <header className="page section story-head">
        <p className="eyebrow">Lore · How this happened</p>
        <h1 className="page-title">The Story</h1>
        <p className="lede">
          Nobody planned this. There was simply a point at which it became irresponsible not to
          write any of it down.
        </p>
      </header>

      <div className="page story-chapters">
        {CHAPTERS.map((chapter) => (
          <section key={chapter.n} className="story-chapter">
            <p className="mono story-chapter-number" aria-hidden="true">
              {chapter.n}
            </p>
            <div className="story-chapter-text">
              <h2 className="story-chapter-title">{chapter.title}</h2>
              {chapter.body.map((paragraph, index) => (
                // Static, hand-authored copy in a fixed order — nothing here is
                // reordered or filtered, so the index is a stable key.
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </section>
        ))}
      </div>

      <section className="page section">
        <div className="card story-cta">
          <p className="eyebrow">05 · Your part</p>
          <h2 className="story-cta-title">Tell someone</h2>
          <p>
            There is no marketing budget, no ad spend, and no algorithm working on our behalf.
            Every single person who has ever ended up here got sent the link by somebody else.
            That is the entire growth plan and it is completely at your mercy.
          </p>
          <p>
            So if a theory got you — send it. Put it in the group chat with no context. Read one
            out loud to somebody at work. The best possible outcome is a stranger having to
            explain to their friends why they now believe the dolphins are watching.
          </p>

          <div className="cluster story-cta-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void share(window.location.origin, 'Shit Max Says')}
            >
              <Icon name={copied ? 'check' : 'link'} size={16} />
              {copied ? 'Link copied' : 'Share the site'}
            </button>
            <Link to="/theories" className="btn btn-ghost">
              Read the theories
              <Icon name="arrowRight" size={16} />
            </Link>
          </div>
        </div>
      </section>
    </article>
  )
}
